import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["otpInput", "verifyBtn", "resendBtn", "messages"]
  static values = { verifyUrl: String, resendUrl: String, redirectUrl: String }

  connect() {
    this.otpInputTarget.focus()
  }

  async verify(event) {
    event.preventDefault()
    const code = this.otpInputTarget.value.trim()
    if (!/^\d{6}$/.test(code)) {
      this.showMessage("Enter the 6-digit code.", "error")
      return
    }

    this.setBusy(this.verifyBtnTarget, "Verifying…")
    try {
      const response = await fetch(this.verifyUrlValue, {
        method: "PATCH",
        headers: this.headers(),
        body: JSON.stringify({ code })
      })
      const data = await response.json()
      if (response.ok && data.status === "verified") {
        this.showMessage("Verified! Redirecting…", "success")
        window.location.href = data.redirect_url || this.redirectUrlValue
      } else {
        this.showMessage(this.errorMessageFor(data.reason, data.retry_after), "error")
      }
    } catch (error) {
      this.showMessage("Something went wrong. Please try again.", "error")
    } finally {
      this.setIdle(this.verifyBtnTarget, "Verify")
    }
  }

  async resend(event) {
    event.preventDefault()
    this.setBusy(this.resendBtnTarget, "Sending…")
    try {
      const response = await fetch(this.resendUrlValue, { method: "POST", headers: this.headers() })
      const data = await response.json()
      if (response.ok && data.status === "sent") {
        this.showMessage("A new code has been sent.", "success")
      } else {
        this.showMessage(this.errorMessageFor(data.reason, data.retry_after), "error")
      }
    } catch (error) {
      this.showMessage("Something went wrong. Please try again.", "error")
    } finally {
      this.setIdle(this.resendBtnTarget, "Resend Code")
    }
  }

  headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
    }
  }

  errorMessageFor(reason, retryAfter) {
    switch (reason) {
      case "expired": return "That code has expired. Request a new one."
      case "invalid": return "Incorrect code. Please try again."
      case "locked_out": return this.lockedOutMessage(retryAfter)
      case "resend_cooldown": return "Please wait before requesting another code."
      case "daily_limit_reached": return "Too many codes requested today. Try again tomorrow."
      default: return "Something went wrong. Please try again."
    }
  }

  lockedOutMessage(retryAfter) {
    if (!retryAfter || retryAfter <= 0) return "Too many failed attempts. Please try again later."
    const hours = Math.floor(retryAfter / 3600)
    const minutes = Math.ceil((retryAfter % 3600) / 60)
    const parts = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
    return `Too many failed attempts. Please try again in ${parts.join(" ")}.`
  }

  setBusy(button, label) {
    button.disabled = true
    button.dataset.originalLabel ||= button.textContent
    button.textContent = label
  }

  setIdle(button, label) {
    button.disabled = false
    button.textContent = button.dataset.originalLabel || label
  }

  showMessage(text, type) {
    const alert = document.createElement("div")
    alert.className = `otp-alert otp-alert--${type}`
    alert.textContent = text
    this.messagesTarget.replaceChildren(alert)
  }
}
